import { createRouter, createWebHashHistory, createWebHistory } from 'vue-router'
// TODO: migrate
// import goTo from 'vuetify/lib/services/goto'

import store from '@/store'
import { awaitGetterUpdate } from '@/utils/store/store-utils'
import { loadAsyncLocale } from '@/plugins/i18n'
import { getBrowserLocale } from '@/utils/i18n'

import auth from '@/routes/auth'
import bookmarks from '@/routes/bookmarks'
import notes from '@/routes/notes'
import collections from '@/routes/collections'
import publicCollections from '@/routes/public'
import account from '@/routes/account'
import legal from '@/routes/legal'
// import {Scroll} from 'vuetify/directives'

const routes = [
  {
    path: '/',
    redirect: '/bookmarks',
  },
  ...(BUILD_TARGET === 'extension' ? [
    {
      path: '/index.html',
      redirect: '/bookmarks',
    },
    {
      path: '/popup.html',
      redirect: '/bookmarks',
    },
  ] : []),
  // extension
  // {
  //   path: '/extension/auth/sso/:provider',
  //   component: () => import(/* webpackChunkName: "auth-ext-setup" */ '@/views/Auth/ExtSetup.vue'),
  //   meta: {
  //     layout: 'fullscreen',
  //     skipOnboarding: true,
  //   },
  // },
  {
    path: '/auth/sso/return',
    meta: {
      noScrollPreservation: true,
      skipOnboarding: true,
    },
  },
  ...publicCollections,
  ...bookmarks,
  ...notes,
  ...collections,
  ...account,
  ...legal,
  ...auth,
]

// Create router instance with appropriate history mode
const router = createRouter({
  history: BUILD_TARGET === 'extension' ? createWebHashHistory(import.meta.env.BASE_URL) : createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior: async (to, from, savedPosition) => {
    let scrollTo = 0

    if (!to.meta.noScrollPreservation) {
      if (savedPosition) {
        scrollTo = savedPosition.y
      } else if (to.hash) {
        try {
          const element = document.querySelector(to.hash)
          if (element) {
            scrollTo = element.offsetTop
          }
        } catch (e) {
          console.warn(`Failed to scroll to hash: ${to.hash}`, e)
        }
      }
    }

    // await sleep(300)
    // TODO: migrate to vue 3
    return { top: scrollTo }
    // return goTo(scrollTo)
  },
})

// Workaround for https://github.com/vitejs/vite/issues/11804
router.onError((err, to) => {
  if (err?.message?.includes?.('Failed to fetch dynamically imported module')) {
    if (!localStorage.getItem('vuetify:dynamic-reload')) {
      console.log('Reloading page to fix dynamic import error')
      localStorage.setItem('vuetify:dynamic-reload', 'true')
      location.assign(to.fullPath)
    } else {
      console.error('Dynamic import error, reloading page did not fix it', err)
    }
  } else {
    console.error(err)
  }
})

router.isReady().then(() => {
  localStorage.removeItem('vuetify:dynamic-reload')
})

// Add custom property to store last route
router.lastRoute = null
// Track when we're navigating back from a legal/settings page to preserve lastRoute
router._isBackNavigation = false

// go to a route with back function if available
router.backTo = to => {
  // check if last route is equal to the route we want to go back to
  // if it is, use push instead of replace to preserve navigation
  const { lastRoute } = router

  if (lastRoute) {
    if (typeof to === 'string' && lastRoute.path === to) {
      router._isBackNavigation = true
      return router.push(to)
    }
    if (typeof to === 'object' && lastRoute.path === to.path) {
      // Deep compare query objects using JSON.stringify
      const lastQuery = JSON.stringify(lastRoute.query || {})
      const toQuery = JSON.stringify(to.query || {})
      if (lastQuery === toQuery) {
        router._isBackNavigation = true
        return router.push(to)
      }
    }
  }

  return router.replace(to)
}

router.beforeEach(async (to, from, next) => {
  // avoid endless error loop
  if (to.name === '404') {
    next()
    return
  }

  // Handle language for public routes
  if (to.meta.isPublicRoute && !store.getters['auth/userId']) {
    const supportedLangs = ['en', 'de']
    const langParam = to.query.lang
    let locale = 'en'

    if (langParam && supportedLangs.includes(langParam)) {
      locale = langParam
    } else {
      const browserLang = getBrowserLocale({ countryCodeOnly: true })
      if (browserLang && supportedLangs.includes(browserLang)) {
        locale = browserLang
      }
    }

    await loadAsyncLocale(locale)
  }

  // consent check for routes that require it (e.g. signup, login) — runs for ALL users
  if (to.meta.requiresConsent && to.path !== '/onboarding') {
    let consent = store.getters['auth/onboardingConsent']
    if (consent === undefined) {
      try {
        consent = await awaitGetterUpdate(store, 'auth/onboardingConsent')
      } catch (error) {
        consent = null
      }
    }

    if (!consent?.ConsentTerms) {
      next({ path: '/onboarding', query: { authRedirect: to.fullPath, ...to.query } })
      return
    }
  }

  // user hasnt completed onboarding (skip for unauthenticated users and public routes)
  const authUserId = store.getters['auth/userId']
  if (!to.meta.skipOnboarding && authUserId) {
    let onboardingConsent = store.getters['auth/onboardingConsent']
    if (onboardingConsent === undefined) {
      try {
        onboardingConsent = await awaitGetterUpdate(store, 'auth/onboardingConsent')
      } catch (error) {
        onboardingConsent = null
      }
    }

    if (!onboardingConsent?.ConsentTerms && to.path !== '/onboarding') {
      const redirectUri = to.fullPath.startsWith('/auth') ? undefined : to.fullPath

      next({
        path: '/onboarding',
        query: { authRedirect: redirectUri, ...to.query },
      })
      return
    }
  }

  // auth check
  if (to.matched.some(record => record.meta.requiresAuth)) {
    let userId = store.getters['auth/userId']

    // auth hasn't been fetched yet
    if (userId === undefined) {
      try {
        userId = await awaitGetterUpdate(store, 'auth/userId')
      } catch (error) {
        userId = null
      }
    }

    // if (still) no auth, redirect to login page.
    if (!userId) {
      next({
        path: '/auth',
        query: { authRedirect: to.fullPath, ...to.query },
      })

      return
    }

  }

  // Bulk action close conditions — runs for all routes (private and public)
  const bulkActionsCloseConditions = [
    { from: /^\/bookmarks$/, exclude: [/^\/bookmarks\/bulk\/add-collections$/, /^\/bookmarks\/bulk\/add-tags$/, /^\/bookmarks\/bulk\/remove-tags$/] },
    { from: /^\/collections\/[^/]+$/, exclude: [/^\/collections\/[^/]+\/bulk\/add-tags$/, /^\/collections\/[^/]+\/bulk\/remove-tags$/, /^\/collections\/[^/]+\/bulk\/edit$/, /^\/collections\/[^/]+\/bulk\/add-collections$/] },
    { from: /^\/collections$/, exclude: [/^\/collections\/bulk\/invite$/, /^\/collections\/bulk\/edit-share-link$/] },
    { from: /^\/bookmarks\/deleted$/, exclude: [] },
    { from: /^\/u\/[^/]+\/collections\/[^/]+$/, exclude: [/^\/collections\/[^/]+\/bulk\/add-collections$/] },
  ]

  if (store.getters['bulkActionsV2/isActive']) {
    bulkActionsCloseConditions.forEach(condition => {
      const isFromMatched = condition.from.test(from.path)
      const isExcluded = condition.exclude.some(regex => regex.test(to.path))

      if (isFromMatched && !isExcluded) {
        store.commit('bulkActionsV2/closeActions')
      }
    })
  }

  // NOTE not in use currently
  // requires full account check
  // if (to.matched.some(record => record.meta.requiresFullAccount)) {
  //   //
  // }

  // TODO some form of way to prevent people from going back to auth route if logged in
  // (only on mobile & extension, otherwise tab is stuck)
  // actually the extension doesn't even have forward/backwards buttons so it shouldn't happen

  next()
})

// keep track of last route
router.afterEach((to, from) => {
  // Don't update lastRoute if we're navigating back from account pages
  // This preserves the original route we want to return to
  if (router._isBackNavigation) {
    router._isBackNavigation = false
    return
  }

  // Store the previous route, but skip storing /account if we're going to a child route
  // This preserves the actual source route when navigating to account subpages
  if (from.path !== '/account' || !to.path.startsWith('/account/')) {
    router.lastRoute = from
  }
})

// Export setup function for Vue 3
export const setupRouter = app => {
  app.use(router)
}

export default router
